'use client'

import { useEffect, useRef, useState } from 'react'
import { usePDFStore } from '../../stores/pdf.store'
import { ImageRenderer } from './ImageRenderer'
import { NativeRenderer, PDFRendererProps } from './NativeRenderer'
import { ScrollableContainer } from './ScrollableContainer'
import { LoadingState } from './LoadingState'
import { cn } from 'ui'

export const PDF_SCROLLABLE_CONTAINER_ID = 'scroll-area-viewport'

export const buildPagePortalId = (documentId: string, page: number) =>
  `document-${documentId}-page-${page}-portal`

export const PDFViewer = ({
  children,
  renderer = 'image',
}: {
  children?: React.ReactNode
  renderer?: 'native' | 'image'
}) => {
  const documents = usePDFStore((pdf) => pdf.documents)
  const zoom = usePDFStore((pdf) => pdf.zoom)
  const update = usePDFStore((pdf) => pdf.update)
  const batchUpdate = usePDFStore((pdf) => pdf.batchUpdate)
  const setPageBoundary = usePDFStore((pdf) => pdf.setPageBoundary)
  const headerHeight = usePDFStore((pdf) => pdf.headerHeight)
  const isIntersectingPage = usePDFStore((pdf) => pdf.isIntersectingPage)
  const isDocumentReady = usePDFStore((pdf) => pdf.isDocumentReady)
  const focusedBlock = usePDFStore((pdf) => pdf.focusedBlock)

  const scrollableContainerContentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    update(
      'scrollableContainerContentHeight',
      scrollableContainerContentRef!.current!.clientHeight
    )
  })

  return (
    <>
      <style>
        {`
          body,
          html {
            overflow: hidden !important;
          }
        `}
      </style>
      <ScrollableContainer
        stickyChildren={children}
        className={cn({
          'cursor-none': !!focusedBlock && isIntersectingPage,
        })}
        headerHeight={headerHeight}
        onScroll={(ev) =>
          batchUpdate({
            scrollLeft: ev.currentTarget.scrollLeft,
            scrollTop: ev.currentTarget.scrollTop,
          })
        }
      >
        {!isDocumentReady && <LoadingState />}
        <div ref={scrollableContainerContentRef} className='w-full h-full'>
          {documents.map((document) => {
            const props: PDFRendererProps = {
              headerHeight,
              zoom,
              pdf: {
                id: document.id,
                name: document.file.original_name,
                url: document.file.url,
                pages: document.pages.map((page) => ({
                  ...page,
                  url: page.file.url,
                })),
              },
              onDocumentReady: () => update('isDocumentReady', true),
              onPageBoundaryChange: setPageBoundary,
              onPageViewportIn: (page) => {
                batchUpdate({
                  currentViewportPageId: document.pages[page].id,
                  currentViewportDocumentId: document.id,
                })
              },
            }

            switch (renderer) {
              case 'image':
                return <ImageRenderer {...props} />

              default:
                return <NativeRenderer {...props} />
            }
          })}
        </div>
      </ScrollableContainer>
    </>
  )
}
